import * as React from 'react';
import InputHandler from 'Components/InputHandler';
import { useIntl } from 'react-intl';
import colors from '../../../../../../config/theme/colors';
import { fm } from 'Components/FormatMessage';
import ItemList from 'Components/ItemList';
import { exists } from 'neuro-utils';
import Unit from 'Components/Unit';
import ActionButton from 'Components/ActionButton';
import { uniq } from 'Utility/ramdaReplacement';
import { Autocomplete } from '@mui/material';
import CircularProgressLocal from 'Components/CircularProgress';
import { TextFieldStaticStyles } from 'Components/InputHandler/components/TextField';
import { ClearIconStyled } from '..';
import type { IExtendedSearchField } from '../ExtendedSearchField';
import type { TSubstanceType } from '../../definitions/common';
import { actions as sessionActions } from 'Store/session';
import { useDispatch } from 'react-redux';

type TSubstanceFieldName = 'substance' | 'status' | 'minDuration' | 'maxDuration' | 'monthsSinceEnd';

const changeSubstance =
  (onChange: (e: TOnChangeValues) => void, substances: 'noCropping' | Array<string>) =>
  (values: TOnChangeValues): void => {
    const valuesName = Object.keys(values)[0];
    if (valuesName !== 'substance') return;
    const value = values[valuesName];
    if (substances === 'noCropping') onChange({ status: 'noCropping', ['substance']: [value as string] });
    if (Array.isArray(substances) && !substances.includes(value ? `${value}` : '')) {
      const newSubstances = [...substances, value];
      onChange({ ['substance']: newSubstances });
    }
  };

const removeSubstance = (
  onChange: (values: TOnChangeValues) => void,
  substances: 'noCropping' | string[],
  index: number,
): void => {
  if (Array.isArray(substances) && substances.length > 1) {
    const newSubstances = substances.slice(0, index).concat(substances.slice(index + 1));
    onChange({ substance: newSubstances });
  } else if (Array.isArray(substances) && substances.length === 1) {
    onChange({ substance: 'noCropping' });
  }
};

export const SubstanceField = ({ platform, searchFields, onChange }: IOwnProps) => {
  const { formatMessage } = useIntl();
  const fmString = (id: string) => formatMessage({ id });

  const [substanceOptions, setSubstanceOptions] = React.useState<string[]>([]);
  const [subSearchSpinner, setSubSearchSpinner] = React.useState<boolean>(false);

  const dispatch = useDispatch();
  const medicationFetch = (searchString: string) =>
    sessionActions.medicationSearch(dispatch, searchString) as unknown as Promise<Record<string, string>[]>;

  const onSubstanceInputChange = (event: React.SyntheticEvent, value: string) => {
    const inputValue = `${value}`;
    if (inputValue.length > 2) {
      setSubSearchSpinner(true);
      void medicationFetch(inputValue).then(
        (res?: Array<{ atc?: string; name?: string; substanceString?: string }>) => {
          setSubSearchSpinner(false);
          if (Array.isArray(res) && res.length > 0) {
            let newOptions: string[] = [];

            res.forEach(medication => {
              const substances = medication.substanceString?.split('; ');
              if (substances && substances.length > 0) newOptions = [...newOptions, ...substances];
            });
            setSubstanceOptions(uniq(newOptions));
          }
        },
      );
    }
  };

  const onChangeSubstances = (i: number) => (values: TOnChangeValues) => {
    const fieldName = Object.keys(values)[0] as TSubstanceFieldName;
    const fieldValue: TFieldValue = values[fieldName];

    // Clone or initialize substance criteria
    const newSubstanceObject = (
      Array.isArray(searchFields.substance) && searchFields.substance.length > 0
        ? structuredClone(searchFields.substance)
        : [{ substance: null }]
    ) as TSubstanceType[];

    const current = newSubstanceObject[i] ?? {};

    for (const fieldName of Object.keys(values) as TSubstanceFieldName[]) {
      const fieldValue = values[fieldName];

      // Handle duration fields
      if (fieldName === 'minDuration' || fieldName === 'maxDuration') {
        const currentDuration = current.durationMonths;
        const updatedDuration = {
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          ...(typeof currentDuration === 'object' && currentDuration !== null ? currentDuration : {}),
          [fieldName]: fieldValue,
        } as Exclude<typeof currentDuration, 'noCropping'>;

        current.durationMonths = updatedDuration;
        continue;
      }

      (current as Record<TSubstanceFieldName, TFieldValue>)[fieldName] = fieldValue;
    }

    // Normalize: empty substance --> 'noCropping'
    if (fieldName === 'substance' && (!Array.isArray(fieldValue) || fieldValue.length === 0)) {
      current.substance = 'noCropping';
    }

    // Normalize: status not 'earlierInUse' --> monthsSinceEnd = 'noCropping'
    if (fieldName === 'status' && fieldValue !== 'earlierInUse') {
      current.monthsSinceEnd = 'noCropping';
    }

    // Normalize: empty durationMonths --> 'noCropping'
    const duration = current.durationMonths;
    if (duration && typeof duration === 'object' && !exists(duration.minDuration) && !exists(duration.maxDuration)) {
      current.durationMonths = 'noCropping';
    }

    // Update and propagate
    newSubstanceObject[i] = current;
    onChange({ substance: newSubstanceObject });
  };

  return (
    <React.Fragment>
      {(Array.isArray(searchFields.substance) ? searchFields.substance : [{ substance: null }]).map((sub, i, arr) => (
        <React.Fragment key={`searchCriteria${i}`}>
          <div
            style={{
              backgroundColor: colors.lightestGray,
              padding: '1rem',
              marginBottom: i === arr.length - 1 ? '1rem' : undefined,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div style={{ marginBottom: '1rem', color: colors.tertiaryText }}>
                {fm('extendedSearch.searchCriteriaTitle')}
                {` ${i + 1}`}
              </div>
              {arr.length > 1 && (
                <ClearIconStyled
                  onClick={() =>
                    Array.isArray(searchFields.substance) &&
                    onChange({ substance: searchFields.substance.filter((_, index) => index !== i) })
                  }
                />
              )}
            </div>

            <React.Fragment>
              <div style={{ fontSize: '1.4rem', color: colors.tertiaryText, marginBottom: '1rem' }}>
                {fm('extendedSearch.chooseSubstance')}
              </div>
              <ItemList
                items={
                  Array.isArray(sub.substance)
                    ? sub.substance.map((subs: string, index: number, array: string[]) => {
                        return {
                          firstItem: subs,
                          itemOnClick: (): void =>
                            removeSubstance(onChangeSubstances(i), sub.substance ?? 'noCropping', index),
                          style: {
                            fontWeight: 600,
                            backgroundColor: colors.lightGray,
                            paddingLeft: '0.8rem',
                            alignItems: 'center',
                            marginBottom: index === array.length - 1 ? '1rem' : undefined,
                          },
                        };
                      })
                    : []
                }
                separator={{
                  content: fm('general.or'),
                  style: { padding: '0.5rem 0 0.5rem 0.8rem', textTransform: 'uppercase' },
                }}
                button={
                  !sub.substance || sub.substance.length === 0 || sub.substance === 'noCropping' ? (
                    <Autocomplete
                      disablePortal
                      options={substanceOptions}
                      noOptionsText={fm('general.noOptions')}
                      sx={{ width: '30rem' }}
                      renderInput={params => (
                        <div ref={params.InputProps.ref} style={{ display: 'flex', alignContent: 'center' }}>
                          <div>
                            <input
                              type="text"
                              placeholder={fmString('extendedSearch.substancePlaceholder')}
                              {...params.inputProps}
                              style={{ ...TextFieldStaticStyles, width: '26.5rem' }}
                            />
                          </div>
                          {subSearchSpinner && (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '3.5rem',
                                padding: '0 1rem',
                              }}
                            >
                              <CircularProgressLocal size={'1.5rem'} />
                            </div>
                          )}
                        </div>
                      )}
                      onInputChange={onSubstanceInputChange}
                      onChange={(event: React.SyntheticEvent, value: string | null) => {
                        value &&
                          changeSubstance(onChangeSubstances(i), sub.substance ?? 'noCropping')({ substance: value });
                      }}
                    />
                  ) : undefined
                }
              />
            </React.Fragment>

            {sub.substance && (
              <React.Fragment>
                <div style={{ fontSize: '1.4rem', color: colors.tertiaryText, margin: '1rem 0 1rem 0' }}>
                  {fm('extendedSearch.status')}
                </div>
                <InputHandler
                  type="Radio"
                  editing={true}
                  name="status"
                  formData={{
                    onChange: onChangeSubstances(i),
                    document: { status: sub.status },
                  }}
                  options={['noCropping', 'inUse', 'earlierInUse']}
                  optionFormatter={(name: string | number): string | React.JSX.Element =>
                    fm(`extendedSearch.opts.${name}`)
                  }
                />
              </React.Fragment>
            )}
            {platform === 'mgravis' && 'status' in sub && (sub.status === 'inUse' || sub.status === 'earlierInUse') && (
              <React.Fragment>
                {(['durationMonths', 'monthsSinceEnd'] as ('durationMonths' | 'monthsSinceEnd')[])
                  .filter(f => sub.status === 'earlierInUse' || f !== 'monthsSinceEnd')
                  .map((f, ii) => (
                    <React.Fragment key={`${f}.${ii}`}>
                      <div style={{ fontSize: '1.4rem', color: colors.tertiaryText, margin: '1rem 0 1rem 0' }}>
                        {fm(`extendedSearch.${f}`)}
                      </div>
                      <Unit unit={fm('extendedSearch.monthsUnit')}>
                        {f === 'durationMonths' ? (
                          <React.Fragment>
                            {(['minDuration', 'maxDuration'] as ('minDuration' | 'maxDuration')[]).map((n, jj) => (
                              <React.Fragment key={jj}>
                                <InputHandler
                                  type="NumberField"
                                  editing={true}
                                  name={n}
                                  formData={{
                                    onChange: onChangeSubstances(i),
                                    document: {
                                      [n]: sub[f] && sub[f] !== 'noCropping' ? sub[f][n] : undefined,
                                    },
                                  }}
                                  min={0}
                                  placeholder={`extendedSearch.${{ 0: 'min', 1: 'max' }[jj]}`}
                                  width={9}
                                />
                                {jj === 0 && ' - '}
                              </React.Fragment>
                            ))}
                          </React.Fragment>
                        ) : (
                          <InputHandler
                            type="NumberField"
                            editing={true}
                            name={f}
                            formData={{
                              onChange: onChangeSubstances(i),
                              document: {
                                [f]: exists(sub[f]) && sub[f] !== 'noCropping' ? sub[f] : undefined,
                              },
                            }}
                            min={0}
                            placeholder={'extendedSearch.min'}
                            width={9}
                          />
                        )}
                      </Unit>
                    </React.Fragment>
                  ))}
              </React.Fragment>
            )}
          </div>
          {arr.length > 1 && i < arr.length - 1 && (
            <div style={{ padding: '1rem', textTransform: 'uppercase' }}>{fm('general.and')}</div>
          )}
        </React.Fragment>
      ))}
      <div style={{ marginTop: '2rem', justifyContent: 'center' }}>
        <ActionButton
          text={'extendedSearch.addSearchCriteria'}
          onClick={() => {
            const newSubtance = structuredClone(
              Array.isArray(searchFields.substance) && searchFields.substance.length > 0
                ? searchFields.substance
                : [{ substance: null }],
            ) as (TSubstanceType | { substance: null })[];
            newSubtance.push({ substance: null });
            onChange({ substance: newSubtance });
          }}
          width={16}
          height={3}
          fontSize={16}
        />
      </div>
    </React.Fragment>
  );
};

interface IOwnProps extends Pick<IExtendedSearchField, 'platform' | 'searchFields' | 'onChange'> {}

export default SubstanceField;
